<template>
  <div v-if="!error_loading">
    <b-card>
      <template #header>
        <h4 class="card-title">{{ $t('message.saved_transactions') }}</h4>
      </template>
      <b-skeleton-table
        v-if="loading"
        :rows="5"
        :columns="5"
        :table-props="{ bordered: true, striped: true }"
      ></b-skeleton-table>
      <b-table
        v-else
        striped
        hover
        :items="items"
        :fields="computedFields"
        :per-page="10"
        :current-page="currentPage"
        responsive
        :busy="loading"
        show-empty
        :empty-text="$t(`message.no_saved_transactions`)"
      >
        <template #cell(trip_information)="data">
          <b-button size="sm" variant="outline-primary" @click="showTripInfo(data.item)">
            View
          </b-button>
        </template>

        <template #cell(resolved)="data">
          <b-badge :variant="data.item.resolved === 'yes' ? 'success' : data.item.resolved === 'no' ? 'danger' : 'secondary'">
            {{ data.item.resolved || 'Not Set' }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-button
            size="sm"
            :variant="isHold(data.item) ? 'danger' : 'warning'"
            :disabled="isRetry"
            @click="retryTransaction(data.item)"
          >
            {{ isHold(data.item) ? 'Approve & Process' : 'Reprocess' }}
          </b-button>
        </template>
      </b-table>

      <!-- Pagination -->
      <div>
        <b-pagination
          v-if="items.length > 0"
          v-model="currentPage"
          :total-rows="items.length"
          :per-page="10"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          class="mb-0 align-items-center"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </div>
    </b-card>

    <!-- Modal: Trip Information -->
    <b-modal v-model="showModal" title="Trip Information" size="lg" scrollable hide-footer>
    <div v-if="parsedTripInfo">
        <b-list-group flush>
        <b-list-group-item><strong>Start Date:</strong> {{ parsedTripInfo.start_date || '—' }}</b-list-group-item>
        <b-list-group-item><strong>End Date:</strong> {{ parsedTripInfo.end_date || '—' }}</b-list-group-item>
        <b-list-group-item><strong>Annual Date:</strong> {{ parsedTripInfo.annual_date || '—' }}</b-list-group-item>
        <b-list-group-item><strong>Product:</strong> {{ parsedTripInfo.product_name || parsedTripInfo.product || '—' }}</b-list-group-item>
        <b-list-group-item><strong>Price:</strong> {{ parsedTripInfo.price || '—' }}</b-list-group-item>
        <b-list-group-item><strong>Residence:</strong> {{ parsedTripInfo.res_name || '—' }}</b-list-group-item>
        <b-list-group-item><strong>Destination:</strong> {{ parsedTripInfo.des_name || '—' }}</b-list-group-item>
        <b-list-group-item><strong>Email:</strong> {{ parsedTripInfo.email || '—' }}</b-list-group-item>
        <b-list-group-item><strong>Address:</strong> {{ parsedTripInfo.address || '—' }}</b-list-group-item>
        <b-list-group-item><strong>Phone:</strong> {{ parsedTripInfo.phone || parsedTripInfo.telephone || parsedTripInfo.mobile || '—' }}</b-list-group-item>
        </b-list-group>

        <!-- Additional Info -->
        <h5 class="mt-4">Additional Info</h5>
        <b-list-group flush>
            <b-list-group-item>
                <strong>Street:</strong>
                {{ parsedTripInfo.street || (parsedTripInfo.additional_info && parsedTripInfo.additional_info.street) || '—' }}
            </b-list-group-item>

            <b-list-group-item>
                <strong>City:</strong>
                {{ parsedTripInfo.city || (parsedTripInfo.additional_info && parsedTripInfo.additional_info.city) || '—' }}
            </b-list-group-item>

            <b-list-group-item>
                <strong>Area:</strong>
                {{ parsedTripInfo.area || (parsedTripInfo.additional_info && parsedTripInfo.additional_info.area) || '—' }}
            </b-list-group-item>

            <b-list-group-item>
                <strong>Postcode:</strong>
                {{ parsedTripInfo.postcode || (parsedTripInfo.additional_info && parsedTripInfo.additional_info.postcode) || '—' }}
            </b-list-group-item>

            <b-list-group-item>
                <strong>Tax ID:</strong>
                {{ parsedTripInfo.tax_identification_number || '—' }}
            </b-list-group-item>

            <b-list-group-item>
                <strong>Company:</strong>
                {{ parsedTripInfo.additional_info && parsedTripInfo.additional_info.company_name || '—' }}
            </b-list-group-item>
        </b-list-group>

        <!-- Travellers -->
        <h5 class="mt-4">Travellers</h5>
        <b-table
        v-if="parsedTripInfo.travellers && parsedTripInfo.travellers.length"
        :items="parsedTripInfo.travellers"
        :fields="travellerFields"
        small
        bordered
        />
        <p v-else>No travellers provided.</p>
    </div>
    <div v-else>
        <p class="text-danger">Trip information is not available or improperly formatted.</p>
    </div>
    </b-modal>

    <!-- Modal: Payment Details -->
    <b-modal v-model="paymentmodalshow" title="Complete Payment" hide-footer>
      <div class="text-center">
        <p><strong>Product:</strong> {{ displayproduct }}</p>
        <p><strong>Price:</strong> {{ displayprice }}</p>
        <p><strong>Payable From:</strong> {{ payablefrom }}</p>
        <p><strong>Payable To:</strong> {{ payableto }}</p>
        <p v-if="display_payment_ammount"><strong>Amount:</strong> {{ payableamount }}</p>
        <b-button
          v-if="hrefpaymentlink"
          :href="hrefpaymentlink"
          variant="success"
          target="_blank"
        >
          Proceed to Payment
        </b-button>
      </div>
    </b-modal>

    <!-- Modal: Payment Error -->
    <b-modal v-model="paymentmodalerrorshow" title="Error" hide-footer>
      <div class="text-danger text-center">
        <p>{{ errorbuyProductModalMsg }}</p>
      </div>
    </b-modal>

    <!-- Optional: Please wait overlay -->
    <b-modal v-if="please_wait" title="Please wait..." hide-footer static lazy centered no-close-on-backdrop no-close-on-esc>
      <div class="text-center p-3">
        <b-spinner class="mb-2" />
        <p>{{ please_wait }}</p>
      </div>
    </b-modal>
  </div>
  <ErrorAlert
    v-else
    :heading="$t('message.couldntloadsavedquotes')"
    :message="$t('message.couldntloadsavedquotesmsg')"
  />
</template>

<script>
import { products } from '@/products';
import ErrorAlert from '../pages/components/ErrorAlert.vue';
import {
  BCard, BTable, BBadge, BPagination, BButton, BListGroup, BListGroupItem, BSkeletonTable, BModal, BSpinner
} from 'bootstrap-vue'
import { performLogoutWithoutRedirect } from '@/auth/logout'
export default {
  components: {
    ErrorAlert,
    BCard,
    BTable,
    BBadge,
    BPagination,
    BButton,
    BListGroup,
    BListGroupItem,
    BSkeletonTable,
    BModal,
    BSpinner
  },
  name: 'SavedTransactions',
  data() {
    return {
      error_loading: false,
      retryTransactionId: null,
      retryToken: null,
      travellerFields: [
        { key: 'title', label: 'Title' },
        { key: 'firstname', label: 'First Name' },
        { key: 'lastname', label: 'Last Name' },
        { key: 'dob', label: 'Date of Birth' },
        { key: 'age', label: 'Age' },
        { key: 'phone', label: 'Phone' },
      ],
      initialRequest:{},
      errorbuyProductModalMsg:'',
      hrefpaymentlink:'',
      paymentmodalerrorshow:false,
      isRetry: false,
      displayproduct:'',
      displayprice:'',
      payablefrom:'',
      payableto:'',
      payableamount:'',
      paymentdts:false,
      paymentmodalshow:false,
      buypolicystatus:true,
      display_payment_ammount: true,
      display_reserve_msg:true,
      display_purchase_msg:false,
      please_wait:'',
      items: [],
      loading: false,
      currentPage: 1,
      showModal: false,
      selectedTripInfo: null,
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'product', label: 'Product', sortable: true },
        { key: 'price', label: 'Price', sortable: true },
        { key: 'reason', label: 'Reason', sortable: true },
        { key: 'resolved', label: 'Resolved', sortable: true },
        { key: 'trip_information', label: 'Trip Info', sortable: false },
        { key: 'actions', label: 'Actions' }
      ]
    };
  },
  computed: {
    cookie() {
      return window?.$cookies?.get("jwt") ?? null;
    },
    token_id() {
      return this.$store.state.app?.userData?.id ?? null
    },
    ls_scope() {
      return this.$store.state.app?.userData?.scope ?? ''
    },
    ls_role() {
      return this.$store.state.app?.userData?.role ?? ''
    },
    parsedTripInfo() {
        try {
        return JSON.parse(this.selectedTripInfo);
        } catch (e) {
        return null;
        }
    },
    buy_policy_text() {
      return this.$t("message.buy_policy");
    },
    // Reason column is visible to admins only
    computedFields() {
      return this.ls_role === 'admin'
        ? this.fields
        : this.fields.filter(f => f.key !== 'reason');
    },
  },
  methods: {
    // A row with a reason was held for review (technical errors have reason = null)
    isHold(item) {
      return !!item.reason;
    },
    async fetchData() {
        this.loading = true;
        const url = 'https://api-quote.safetravel.today/v2/app/get_saved_transactions';

        const data = {
            token: this.token_id,
            jwt: this.cookie
        }
        try {
            const response = await fetch(url, {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
            });
            if (!response.ok) throw new Error(`Failed to fetch saved transactions: HTTP ${response.status}`);
            const result = await response.json();

            if (result === 'not_valid') {
              performLogoutWithoutRedirect(this.$store);
              return;
            }
            // Non-admins only see technical-error rows (reason = null).
            // Held rows (sanctions) are visible to admins only.
            const rows = (result || []).filter(item => this.ls_role === 'admin' || !item.reason);
            this.items = rows.map(item => {
                if (item.product && typeof item.product === 'string') {
                    let productCode = item.product;
                    let price = '';

                    if (productCode.includes(':')) {
                    [productCode, price] = productCode.split(':');
                    }

                    return {
                    ...item,
                    product: products[productCode] || productCode,  // fallback to raw code if no label found
                    price: price || ''
                    };
                }
                return {
                    ...item,
                    product: '',
                    price: ''
                };
            });
        } catch (error) {
            this.error_loading = true;
            console.error('Failed to load saved transactions:', error);
        } finally {
            this.loading = false;
        }
    },
    showTripInfo(item) {
      this.selectedTripInfo = item.trip_information;
      this.showModal = true;
    },
    async markTransactionResolved(id) {
      const url = 'https://api-quote.safetravel.today/v2/app/resolve_saved_transactions';
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jwt: this.cookie,
            token: this.token_id,
            id: id
          }),
        });
        if (!response.ok) {
          console.warn('Failed to resolve transaction - bad status:', response.status);
          return;
        }
        const resJson = await response.json();

        // If token is invalid, log out
        if (resJson?.error === 'not_valid') {
          performLogoutWithoutRedirect(this.$store);
          return;
        }
        this.items = this.items.filter(item => item.id !== id);
      } catch (err) {
        console.warn('Failed to mark transaction as resolved:', err);
      }
    },
    async retryTransaction(tx) {
      // Held transactions (admin only) need a confirmation
      if (this.isHold(tx) && !window.confirm('This request was held for review. Approve and process it?')) {
        return;
      }
      try {
          this.isRetry = true;
          this.initialRequest = JSON.parse(tx.trip_information);
          this.retryToken = this.initialRequest?.token_id || tx.token || this.token_id;
          this.retryTransactionId = tx.id;

          this.please_wait = 'Retrying...';
          const dummyEvent = { target: { innerHTML: '', disabled: false } };

          if (this.ls_scope.includes('app.travel-insurance.purchase')) {
              await this.purchaseURL(dummyEvent);
          } else {
              await this.reserveURL(dummyEvent);
          }
      } catch (err) {
          console.error('Failed to retry transaction:', err);
          this.errorbuyProductModalMsg = 'Retry failed. Please check the saved transaction.';
          this.paymentmodalerrorshow = true;
      } finally {
          this.isRetry = false;
          this.retryTransactionId = null;
          this.retryToken = null;
          this.please_wait = '';
      }
    },
    async purchaseURL(e) {
        const url = 'https://api-quote.safetravel.today/v2/s360/purchase';
        const paymentOrderUrl = 'https://api-quote.safetravel.today/v2/app/payment-order/generate';

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(this.initialRequest),
            });

            if (!response.ok) {
                this.errorbuyProductModalMsg = `API request failed with status ${response.status}. Please try again later.`;
                this.paymentmodalerrorshow = true;
                return;
            }

            const result = await response.json();

            if (result.error || !result?.token) {
                throw new Error(result.error || 'Unexpected response. Please try again later.');
            }

            const transactionId = Number(result.transaction_id);
            if (isNaN(transactionId)) throw new Error('Invalid transaction ID received');
            const transaction_ids = [transactionId];

            this.displayproduct = this.initialRequest?.product_name || 'Unknown Product';
            this.displayprice = result?.gross_price ? `${result.gross_price}€` : 'N/A';
            this.payablefrom = result?.payable_from || 'N/A';
            this.payableto = result?.payable_to || 'N/A';
            this.display_payment_ammount = result.payable_here !== '-';
            this.display_reserve_msg = false;
            this.display_purchase_msg = true;

            const paymentToken = this.retryToken || this.token_id;
            if (!paymentToken) throw new Error('Token ID is missing');

            const data_viva = {
                token: paymentToken,
                env: 'production',
                transaction_ids,
            };

            const vivaResponse = await fetch(paymentOrderUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data_viva),
            });

            if (!vivaResponse.ok) {
                throw new Error(`Payment order API failed with status ${vivaResponse.status}`);
            }

            const vivaResult = await vivaResponse.json();
            const amount = vivaResult?.payment_order?.paymentOrder?.amount
            ? (vivaResult.payment_order.paymentOrder.amount / 100).toFixed(2)
            : null;

            if (!amount || !vivaResult?.payment_order?.checkoutUrl) {
                throw new Error('Invalid payment order response');
            }

            this.payableamount = `${amount}€`;
            this.hrefpaymentlink = vivaResult.payment_order.checkoutUrl;
            this.paymentdts = true;
            this.paymentmodalshow = true;
            this.buypolicystatus = true;
            // Mark transaction resolved only if everything above succeeds
            if (this.isRetry && this.retryTransactionId) {
              await this.markTransactionResolved(this.retryTransactionId);
            }
            e.target.innerHTML = this.buy_policy_text;
            e.target.disabled = false;
        } catch (error) {
            console.error('Error in purchaseURL:', error);
            this.errorbuyProductModalMsg = error.message || 'An error occurred. Please try again.';
            this.paymentmodalerrorshow = true;
            e.target.innerHTML = this.buy_policy_text;
            e.target.disabled = false;
        }
    },
    async reserveURL(e) {
        const url = 'https://api-quote.safetravel.today/v2/app/reserve';

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(this.initialRequest),
            });

            if (!response.ok) {
                this.errorbuyProductModalMsg = `API request failed with status ${response.status}. Please try again later.`;
                this.paymentmodalerrorshow = true;
                return;
            }

            const result = await response.json();

            if (result.error || !result?.token) {
                throw new Error(result.error || 'Unexpected response. Please try again later.');
            }

            this.displayproduct = this.initialRequest?.product_name || 'Unknown Product';
            this.displayprice = result.gross_price ? `${result.gross_price}€` : 'N/A';
            this.payablefrom = result.payable_from || 'N/A';
            this.payableto = result.payable_to || 'N/A';
            this.payableamount = result.payable_amount ? `${result.payable_amount}€` : 'N/A';
            this.hrefpaymentlink = result.payable_here || '';
            this.display_payment_ammount = result.payable_here !== '-';
            this.display_reserve_msg = true;
            this.display_purchase_msg = false;

            this.paymentdts = true;
            this.paymentmodalshow = true;
            this.buypolicystatus = true;
            // Mark transaction resolved only if everything above succeeds
            if (this.isRetry && this.retryTransactionId) {
              await this.markTransactionResolved(this.retryTransactionId);
            }
            e.target.innerHTML = this.buy_policy_text;
            e.target.disabled = false;
        } catch (error) {
            console.error('Error in reserveURL:', error);
            this.errorbuyProductModalMsg = error.message || 'An error occurred during reservation. Please try again.';
            this.paymentmodalerrorshow = true;
            e.target.innerHTML = this.buy_policy_text;
            e.target.disabled = false;
        }
    },
  },
  created() {
    this.fetchData();
  },
};
</script>

<style scoped>
::v-deep .card-title {
  color: var(--foreground-color);
  font-size: 1.65rem;
}
pre {
  background-color: var(--bg-color);
  padding: 1rem;
  border-radius: 5px;
}
</style>